import { useEffect, useState, useCallback } from "react"
import {
  IconUserCheck,
  IconLoader2,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { api } from "@/lib/api"
import { toast } from "sonner"

interface ReviewerDto {
  userId: string
  displayName: string
}

interface UserDto {
  id: string
  displayName: string
  userPrincipalName: string
}

export default function AdminReviewersPage() {
  const [reviewers, setReviewers] = useState<ReviewerDto[]>([])
  const [allUsers, setAllUsers] = useState<UserDto[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState("")

  const loadReviewers = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api.get<ReviewerDto[]>(
        "/api/people/change-requests/reviewers"
      )
      setReviewers(data)
    } catch (err) {
      console.error("Failed to load reviewers:", err)
      toast.error("Failed to load reviewers.")
    } finally {
      setLoading(false)
    }
  }, [])

  const loadUsers = useCallback(async () => {
    try {
      const data = await api.get<UserDto[]>("/api/users")
      setAllUsers(data)
    } catch {
      // Non-critical
    }
  }, [])

  useEffect(() => {
    loadReviewers()
    loadUsers()
  }, [loadReviewers, loadUsers])

  const saveReviewers = async (newList: ReviewerDto[]) => {
    setSaving(true)
    try {
      await api.put("/api/people/change-requests/reviewers", {
        userIds: newList.map((r) => r.userId),
      })
      setReviewers(newList)
      toast.success("Reviewers updated.")
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save."
      toast.error(message)
      loadReviewers()
    } finally {
      setSaving(false)
    }
  }

  const handleAdd = () => {
    if (!selectedUserId) return
    const user = allUsers.find((u) => u.id === selectedUserId)
    if (!user) return
    if (reviewers.some((r) => r.userId === selectedUserId)) {
      toast.error("This user is already a reviewer.")
      return
    }
    const newList = [
      ...reviewers,
      { userId: user.id, displayName: user.displayName },
    ]
    saveReviewers(newList)
    setAddDialogOpen(false)
    setSelectedUserId("")
  }

  const handleRemove = (userId: string) => {
    const newList = reviewers.filter((r) => r.userId !== userId)
    saveReviewers(newList)
  }

  const availableUsers = allUsers.filter(
    (u) => !reviewers.some((r) => r.userId === u.id)
  )

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <IconUserCheck className="size-6" />
          <h1 className="text-2xl font-semibold tracking-tight">
            Change Request Reviewers
          </h1>
          <Badge variant="secondary">{reviewers.length} reviewers</Badge>
        </div>
        <Button onClick={() => setAddDialogOpen(true)} disabled={saving}>
          <IconPlus className="size-4 mr-2" />
          Add Reviewer
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Eligible Reviewers</CardTitle>
          <CardDescription>
            Staff members who can review person change requests. When a change
            request is created, it is assigned to the reviewer with the fewest
            pending requests.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {reviewers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <IconUserCheck className="size-12 mx-auto mb-4 opacity-30" />
              <p>No reviewers configured.</p>
              <p className="text-sm mt-1">
                Change requests will be self-assigned to the requester until
                reviewers are added.
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviewers.map((r) => (
                    <TableRow key={r.userId}>
                      <TableCell className="font-medium">
                        {r.displayName}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleRemove(r.userId)}
                          disabled={saving}
                        >
                          <IconTrash className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Reviewer Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Reviewer</DialogTitle>
            <DialogDescription>
              Select a staff member to add as an eligible change request
              reviewer.
            </DialogDescription>
          </DialogHeader>
          <Select value={selectedUserId} onValueChange={setSelectedUserId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a user" />
            </SelectTrigger>
            <SelectContent>
              {availableUsers.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAddDialogOpen(false)
                setSelectedUserId("")
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={!selectedUserId || saving}
            >
              {saving && <IconLoader2 className="size-4 mr-2 animate-spin" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
